import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  Search,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  X,
  Users,
  Calendar,
  Globe,
  Mail,
  Phone,
  Palette,
  UserPlus,
  ArrowLeft,
  Edit,
  MoreVertical,
  KeyRound,
  UserMinus,
  UserX,
  Shield,
  RotateCcw,
  Upload,
  Image
} from 'lucide-react'
import {
  adminApi,
  UpdateOrganizationRequest,
  OrganizationMember,
  CreateLicenseRequest,
  contractAdminApi,
  AdminContractRequest,
  AdminPaymentRequest,
  AdminPaymentDto,
  AdminUser
} from '@/api/admin'
import { toast } from 'sonner'
import ResetDataModal from '@/components/ResetDataModal'

/** 현재 로컬 날짜를 YYYY-MM-DD 포맷으로 반환 */
function getLocalDateString(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const ORGANIZATION_TYPES = [
  { value: 'company', label: '기업' },
  { value: 'learning_center', label: 'Learning Center' },
  { value: 'academy', label: '학원' },
  { value: 'school', label: '학교' },
  { value: 'institution', label: '기관' },
  { value: 'other', label: '기타' }
]

const ORGANIZATION_TYPE_LABELS: Record<string, string> = {
  company: '기업',
  learning_center: 'Learning Center',
  academy: '학원',
  school: '학교',
  institution: '기관',
  other: '기타'
}

const LANGUAGE_MODE_OPTIONS = [
  { value: 'ko', label: '한국어' },
  { value: 'en', label: 'English' },
  { value: 'both', label: '한국어 + English' },
]

const LANGUAGE_MODE_LABELS: Record<string, string> = {
  ko: '한국어',
  en: 'English',
  both: '한국어 + English',
}

const USER_TYPE_LABELS: Record<string, string> = {
  individual: '개인 (B2C)',
  parent: '학부모',
  student: '학생',
  employee: '기업 (B2B)',
  teacher: 'Learning Center',
  admin: '깨봉 Admin'
}

const getFullDomain = (subdomain: string | null, organizationType: string): string | null => {
  if (!subdomain) return null
  if (organizationType === 'learning_center') {
    return `${subdomain}.quebonLC.tv`
  }
  return `${subdomain}.aiqlab.tv`
}

const DEV_PORT_BY_TYPE: Record<string, number> = {
  'company': 3300,
  'learning_center': 3100,
}

const getStagingUrl = (subdomain: string | null, organizationType: string): string | null => {
  if (!subdomain) return null
  if (organizationType === 'learning_center') {
    return `${subdomain}.staging.quebonlc.tv`
  }
  return `${subdomain}.staging.aiqlab.tv`
}

const getDevUrl = (subdomain: string | null, organizationType: string): string | null => {
  if (!subdomain) return null
  const port = DEV_PORT_BY_TYPE[organizationType] || DEV_PORT_BY_TYPE['company']
  return `${subdomain}.localhost:${port}`
}

// ============================================================================
// Contract Tab Component
// ============================================================================

function ContractTab({ organizationId, members }: { organizationId: number; members: OrganizationMember[] }) {
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [contractForm, setContractForm] = useState<AdminContractRequest>({})
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null)
  const [isAddingPayment, setIsAddingPayment] = useState(false)
  const [paymentForm, setPaymentForm] = useState<AdminPaymentRequest>({
    paidAt: getLocalDateString(),
    amount: 0,
    paymentMethod: 'bank_transfer',
  })

  const { data: contractData, isLoading } = useQuery({
    queryKey: ['admin-contract', organizationId],
    queryFn: () => contractAdminApi.getContract(organizationId),
  })

  const contract = contractData?.data?.data

  const createMutation = useMutation({
    mutationFn: (data: AdminContractRequest) => contractAdminApi.createContract(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contract', organizationId] })
      setIsEditing(false)
      toast.success('계약이 생성되었습니다.')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: AdminContractRequest) => contractAdminApi.updateContract(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contract', organizationId] })
      setIsEditing(false)
      toast.success('계약 정보가 수정되었습니다.')
    },
  })

  const addPaymentMutation = useMutation({
    mutationFn: (data: AdminPaymentRequest) => contractAdminApi.addPayment(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contract', organizationId] })
      setIsAddingPayment(false)
      setPaymentForm({ paidAt: getLocalDateString(), amount: 0, paymentMethod: 'bank_transfer' })
      toast.success('결제가 추가되었습니다.')
    },
  })

  const updatePaymentMutation = useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: AdminPaymentRequest }) =>
      contractAdminApi.updatePayment(organizationId, paymentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contract', organizationId] })
      setEditingPaymentId(null)
      toast.success('결제가 수정되었습니다.')
    },
  })

  const deletePaymentMutation = useMutation({
    mutationFn: (paymentId: string) => contractAdminApi.deletePayment(organizationId, paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contract', organizationId] })
      toast.success('결제가 삭제되었습니다.')
    },
  })

  if (isLoading) return <div className="py-8 text-center text-gray-400">로딩 중...</div>

  const startEdit = () => {
    setContractForm({
      buyerUserId: contract?.contractContact?.userId,
      contractStatus: contract?.contractStatus || 'active',
      totalAmount: contract?.contractAmount || undefined,
      startDate: contract?.startDate || undefined,
      endDate: contract?.endDate || undefined,
      notes: contract?.notes || undefined,
    })
    setIsEditing(true)
  }

  const handleSaveContract = () => {
    if (contract?.contractId) {
      updateMutation.mutate(contractForm)
    } else {
      createMutation.mutate(contractForm)
    }
  }

  const startEditPayment = (payment: AdminPaymentDto) => {
    setEditingPaymentId(payment.paymentId)
    setPaymentForm({
      paidAt: payment.paidAt || getLocalDateString(),
      amount: payment.amount,
      paymentMethod: payment.paymentMethod,
      notes: payment.notes || undefined,
    })
  }

  const handleDeletePayment = (paymentId: string) => {
    if (confirm('이 결제를 삭제하시겠습니까?')) {
      deletePaymentMutation.mutate(paymentId)
    }
  }

  const formatAmount = (amount: number | null) => amount !== null && amount !== undefined ? amount.toLocaleString('ko-KR') + '원' : '-'
  const formatDate = (d: string | null) => {
    if (!d) return '-'
    try { return new Date(d).toLocaleDateString('ko-KR') } catch { return d }
  }

  return (
    <div className="space-y-6">
      {/* 계약 정보 */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-gray-900">계약 정보</h4>
          {isEditing ? (
            <div className="flex gap-2">
              <button onClick={() => setIsEditing(false)} className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50">취소</button>
              <button onClick={handleSaveContract} className="px-3 py-1 text-xs text-white bg-indigo-600 rounded hover:bg-indigo-700">저장</button>
            </div>
          ) : (
            <button onClick={startEdit} className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50">
              {contract?.contractId ? '수정' : '생성'}
            </button>
          )}
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-4">
            <span className="w-24 text-gray-500">계약 담당자</span>
            {isEditing ? (
              <select
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                value={contractForm.buyerUserId || ''}
                onChange={(e) => setContractForm({ ...contractForm, buyerUserId: e.target.value ? Number(e.target.value) : undefined })}
              >
                <option value="">선택</option>
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>{m.fullName} ({m.email})</option>
                ))}
              </select>
            ) : (
              <span className="text-gray-900">{contract?.contractContact?.name || '-'} {contract?.contractContact?.email ? `(${contract.contractContact.email})` : ''}</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="w-24 text-gray-500">계약 상태</span>
            {isEditing ? (
              <select
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                value={contractForm.contractStatus || 'active'}
                onChange={(e) => setContractForm({ ...contractForm, contractStatus: e.target.value })}
              >
                <option value="active">활성</option>
                <option value="expired">만료</option>
                <option value="terminated">해지</option>
              </select>
            ) : (
              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                contract?.contractStatus === 'active' ? 'bg-green-100 text-green-700' :
                contract?.contractStatus === 'expired' ? 'bg-yellow-100 text-yellow-700' :
                contract?.contractStatus === 'terminated' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {contract?.contractStatus === 'active' ? '활성' : contract?.contractStatus === 'expired' ? '만료' : contract?.contractStatus === 'terminated' ? '해지' : '-'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="w-24 text-gray-500">계약 금액</span>
            {isEditing ? (
              <input
                type="number"
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                value={contractForm.totalAmount || ''}
                onChange={(e) => setContractForm({ ...contractForm, totalAmount: e.target.value ? Number(e.target.value) : undefined })}
              />
            ) : (
              <span className="text-gray-900">{formatAmount(contract?.contractAmount || null)}</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="w-24 text-gray-500">시작일</span>
            {isEditing ? (
              <input
                type="date"
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                value={contractForm.startDate || ''}
                onChange={(e) => setContractForm({ ...contractForm, startDate: e.target.value || undefined })}
              />
            ) : (
              <span className="text-gray-900">{contract?.startDate || '-'}</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="w-24 text-gray-500">종료일</span>
            {isEditing ? (
              <input
                type="date"
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                value={contractForm.endDate || ''}
                onChange={(e) => setContractForm({ ...contractForm, endDate: e.target.value || undefined })}
              />
            ) : (
              <span className="text-gray-900">{contract?.endDate || '-'}</span>
            )}
          </div>
          <div className="flex items-start gap-4">
            <span className="w-24 text-gray-500 pt-1">메모</span>
            {isEditing ? (
              <textarea
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                rows={2}
                value={contractForm.notes || ''}
                onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })}
              />
            ) : (
              <span className="text-gray-900">{contract?.notes || '-'}</span>
            )}
          </div>
        </div>
      </div>

      {/* 결제 내역 */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-gray-900">결제 내역</h4>
          {contract?.contractId && (
            <button
              onClick={() => { setIsAddingPayment(true); setPaymentForm({ paidAt: getLocalDateString(), amount: 0, paymentMethod: 'bank_transfer' }) }}
              className="px-3 py-1 text-xs text-white bg-indigo-600 rounded hover:bg-indigo-700"
            >
              추가
            </button>
          )}
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium">결제일자</th>
              <th className="pb-2 font-medium text-right">결제금액</th>
              <th className="pb-2 font-medium">방식</th>
              <th className="pb-2 font-medium">메모</th>
              <th className="pb-2 font-medium text-right">액션</th>
            </tr>
          </thead>
          <tbody>
            {isAddingPayment && (
              <tr className="border-b">
                <td className="py-2"><input type="date" className="border rounded px-1 py-0.5 text-xs w-full" value={paymentForm.paidAt} onChange={(e) => setPaymentForm({ ...paymentForm, paidAt: e.target.value })} /></td>
                <td className="py-2"><input type="number" className="border rounded px-1 py-0.5 text-xs w-full text-right" value={paymentForm.amount || ''} onChange={(e) => setPaymentForm({ ...paymentForm, amount: Number(e.target.value) })} /></td>
                <td className="py-2">
                  <select className="border rounded px-1 py-0.5 text-xs" value={paymentForm.paymentMethod} onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}>
                    <option value="bank_transfer">입금</option>
                    <option value="credit_card">카드</option>
                  </select>
                </td>
                <td className="py-2"><input type="text" className="border rounded px-1 py-0.5 text-xs w-full" placeholder="메모" value={paymentForm.notes || ''} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value || undefined })} /></td>
                <td className="py-2 text-right space-x-1">
                  <button onClick={() => addPaymentMutation.mutate(paymentForm)} className="text-xs text-indigo-600 hover:underline">저장</button>
                  <button onClick={() => setIsAddingPayment(false)} className="text-xs text-gray-400 hover:underline">취소</button>
                </td>
              </tr>
            )}
            {(contract?.payments || []).map((p) => (
              <tr key={p.paymentId} className="border-b">
                {editingPaymentId === p.paymentId ? (
                  <>
                    <td className="py-2"><input type="date" className="border rounded px-1 py-0.5 text-xs w-full" value={paymentForm.paidAt} onChange={(e) => setPaymentForm({ ...paymentForm, paidAt: e.target.value })} /></td>
                    <td className="py-2"><input type="number" className="border rounded px-1 py-0.5 text-xs w-full text-right" value={paymentForm.amount || ''} onChange={(e) => setPaymentForm({ ...paymentForm, amount: Number(e.target.value) })} /></td>
                    <td className="py-2">
                      <select className="border rounded px-1 py-0.5 text-xs" value={paymentForm.paymentMethod} onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}>
                        <option value="bank_transfer">입금</option>
                        <option value="credit_card">카드</option>
                      </select>
                    </td>
                    <td className="py-2"><input type="text" className="border rounded px-1 py-0.5 text-xs w-full" placeholder="메모" value={paymentForm.notes || ''} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value || undefined })} /></td>
                    <td className="py-2 text-right space-x-1">
                      <button onClick={() => updatePaymentMutation.mutate({ paymentId: p.paymentId, data: paymentForm })} className="text-xs text-indigo-600 hover:underline">저장</button>
                      <button onClick={() => setEditingPaymentId(null)} className="text-xs text-gray-400 hover:underline">취소</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-2 text-gray-900">{formatDate(p.paidAt)}</td>
                    <td className="py-2 text-gray-900 text-right">{formatAmount(p.amount)}</td>
                    <td className="py-2 text-gray-600">{p.paymentMethod === 'bank_transfer' ? '입금' : p.paymentMethod === 'credit_card' ? '카드' : p.paymentMethod}</td>
                    <td className="py-2 text-gray-600">{p.notes || '-'}</td>
                    <td className="py-2 text-right space-x-1">
                      <button onClick={() => startEditPayment(p)} className="text-xs text-indigo-600 hover:underline">수정</button>
                      <button onClick={() => handleDeletePayment(p.paymentId)} className="text-xs text-red-500 hover:underline">삭제</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {contract?.contractId && (
          <div className="mt-3 text-right text-sm font-semibold text-gray-900">
            총 결제액: {formatAmount(contract.totalPaidAmount)}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Main Page Component
// ============================================================================

export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const organizationId = Number(id)

  const [activeTab, setActiveTab] = useState<'info' | 'licenses' | 'members' | 'contract'>('info')
  type EditSection = 'basic' | 'contact' | 'access' | 'contract' | 'branding'
  const [editingSection, setEditingSection] = useState<EditSection | null>(null)
  const [infoForm, setInfoForm] = useState<UpdateOrganizationRequest>({})
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [showLicenseForm, setShowLicenseForm] = useState(false)
  const [licenseForm, setLicenseForm] = useState<CreateLicenseRequest>({
    productId: 0,
    validFrom: getLocalDateString(),
    validUntil: ''
  })

  // Fetch organization
  const { data: organization, isLoading, error } = useQuery({
    queryKey: ['admin', 'organizations', organizationId],
    queryFn: () => adminApi.getOrganizationById(organizationId),
    enabled: !isNaN(organizationId)
  })

  // Fetch licenses
  const { data: licenses = [], isLoading: licensesLoading } = useQuery({
    queryKey: ['admin', 'organizations', organizationId, 'licenses'],
    queryFn: () => adminApi.getOrganizationLicenses(organizationId),
    enabled: activeTab === 'licenses' && !isNaN(organizationId)
  })

  // Fetch members
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['admin', 'organizations', organizationId, 'members'],
    queryFn: () => adminApi.getOrganizationMembers(organizationId),
    enabled: (activeTab === 'members' || activeTab === 'contract') && !isNaN(organizationId)
  })

  // Fetch products for license form
  const { data: products = [] } = useQuery({
    queryKey: ['admin', 'products', 'all'],
    queryFn: () => adminApi.getProducts({ size: 100 }).then(r => r.content),
    enabled: showLicenseForm
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateOrganizationRequest }) =>
      adminApi.updateOrganization(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      setEditingSection(null)
      toast.success('기관 정보가 수정되었습니다.')
    },
    onError: (error: Error) => {
      console.error('Update organization error:', error)
      toast.error('기관 수정에 실패했습니다.')
    }
  })

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: adminApi.deactivateOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      toast.success('기관이 비활성화되었습니다.')
      navigate('/organizations')
    }
  })

  // Grant license mutation
  const grantLicenseMutation = useMutation({
    mutationFn: (data: CreateLicenseRequest) => adminApi.grantLicense(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'licenses'] })
      setShowLicenseForm(false)
      setLicenseForm({ productId: 0, validFrom: getLocalDateString(), validUntil: '' })
    }
  })

  // Revoke license mutation
  const revokeLicenseMutation = useMutation({
    mutationFn: adminApi.revokeLicense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'licenses'] })
    }
  })

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: (userId: number) => adminApi.resetMemberPassword(organizationId, userId),
    onSuccess: (data) => {
      alert(`임시 비밀번호: ${data.temporaryPassword}\n\n사용자에게 전달해주세요.`)
    },
    onError: () => {
      alert('비밀번호 리셋에 실패했습니다.')
    }
  })

  // Add member modal state
  const [showAddMemberModal, setShowAddMemberModal] = useState(false)
  const [memberSearchKeyword, setMemberSearchKeyword] = useState('')
  const [memberSearchType, setMemberSearchType] = useState<string>('')
  const [selectedRole, setSelectedRole] = useState('member')
  const [addMemberMode, setAddMemberMode] = useState<'search' | 'create'>('search')
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    fullName: '',
    userType: 'employee' as 'individual' | 'parent' | 'student' | 'employee' | 'teacher' | 'admin',
    password: '',
  })

  // Search users for add member
  const { data: searchUsersResult, isLoading: searchUsersLoading } = useQuery({
    queryKey: ['admin', 'users', 'search', memberSearchKeyword, memberSearchType],
    queryFn: () => adminApi.getUsers({
      keyword: memberSearchKeyword,
      userType: memberSearchType ? memberSearchType as any : undefined,
      size: 20
    }),
    enabled: showAddMemberModal && addMemberMode === 'search' && memberSearchKeyword.length >= 2
  })

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: (data: { userId: number; role: string }) =>
      adminApi.addOrganizationMember(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'members'] })
      setShowAddMemberModal(false)
      setMemberSearchKeyword('')
      setMemberSearchType('')
      setSelectedRole('member')
      setAddMemberMode('search')
      toast.success('멤버가 추가되었습니다.')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || '멤버 추가에 실패했습니다.'
      toast.error(message)
    }
  })

  // Create user and add as member mutation
  const createAndAddMemberMutation = useMutation({
    mutationFn: async (data: { email: string; fullName: string; userType: string; password: string; role: string }) => {
      const newUser = await adminApi.createUser({
        email: data.email,
        fullName: data.fullName,
        userType: data.userType as any,
        password: data.password || undefined,
      })
      await adminApi.addOrganizationMember(organizationId, { userId: newUser.id, role: data.role })
      return newUser
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'members'] })
      setShowAddMemberModal(false)
      setMemberSearchKeyword('')
      setMemberSearchType('')
      setSelectedRole('member')
      setAddMemberMode('search')
      setNewUserForm({ email: '', fullName: '', userType: 'employee', password: '' })
      toast.success('사용자가 생성되고 멤버로 추가되었습니다.')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message || '사용자 생성에 실패했습니다.'
      toast.error(message)
    }
  })

  const handleAddMember = (userId: number) => {
    addMemberMutation.mutate({ userId, role: selectedRole })
  }

  const handleCreateAndAddMember = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newUserForm.email || !newUserForm.fullName) {
      toast.error('이메일과 이름은 필수입니다.')
      return
    }
    createAndAddMemberMutation.mutate({
      ...newUserForm,
      role: selectedRole,
    })
  }

  const memberUserIds = new Set(members.map(m => m.userId))

  const handleRevokeLicense = (licenseId: number) => {
    if (confirm('정말 이 라이선스를 취소하시겠습니까?')) {
      revokeLicenseMutation.mutate(licenseId)
    }
  }

  // Remove member from organization mutation
  const removeMemberMutation = useMutation({
    mutationFn: (userId: number) => adminApi.removeOrganizationMember(organizationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'members'] })
      toast.success('멤버가 조직에서 제거되었습니다.')
    },
    onError: () => {
      toast.error('멤버 제거에 실패했습니다.')
    }
  })

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      await adminApi.deleteUser(userId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'members'] })
      toast.success('사용자가 완전히 삭제되었습니다.')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || '사용자 삭제에 실패했습니다.'
      toast.error(message)
    }
  })

  // Update member role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: string }) =>
      adminApi.updateMemberRole(organizationId, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'members'] })
      toast.success('역할이 변경되었습니다.')
    },
    onError: () => {
      toast.error('역할 변경에 실패했습니다.')
    }
  })

  const [confirmModal, setConfirmModal] = useState<{
    type: 'removeMember' | 'deleteUser'
    member: OrganizationMember
  } | null>(null)

  const [roleModal, setRoleModal] = useState<{
    member: OrganizationMember
    selectedRole: string
  } | null>(null)

  const handleResetPassword = (member: OrganizationMember) => {
    if (confirm(`"${member.fullName}" 사용자의 비밀번호를 리셋하시겠습니까?`)) {
      resetPasswordMutation.mutate(member.userId)
    }
  }

  const handleGrantLicense = (e: React.FormEvent) => {
    e.preventDefault()
    if (licenseForm.productId === 0) {
      alert('상품을 선택해주세요.')
      return
    }
    grantLicenseMutation.mutate(licenseForm)
  }

  const handleDeactivate = () => {
    if (!organization) return
    if (confirm(`정말 "${organization.name}" 기관을 비활성화하시겠습니까?\n\n이 작업은 신중하게 진행해주세요.`)) {
      deactivateMutation.mutate(organization.id)
    }
  }

  const startEditSection = (section: EditSection) => {
    if (!organization) return
    setInfoForm({
      name: organization.name,
      displayName: organization.displayName || '',
      representativeName: organization.representativeName || '',
      organizationType: organization.organizationType,
      businessCategory: organization.businessCategory || '',
      subdomain: organization.subdomain || '',
      email: organization.email || '',
      phoneNumber: organization.phoneNumber || '',
      websiteUrl: organization.websiteUrl || '',
      logoUrl: organization.logoUrl || '',
      primaryColor: organization.primaryColor || '#3B82F6',
      contractStartDate: organization.contractStartDate || '',
      contractEndDate: organization.contractEndDate || '',
      maxSeats: organization.maxSeats || 0,
      languageMode: organization.languageMode || 'ko',
    })
    if (section === 'branding') {
      setLogoPreview(organization.logoUrl || null)
    }
    setEditingSection(section)
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!organization) return
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (ev) => {
      setLogoPreview(ev.target?.result as string)
    }
    reader.readAsDataURL(file)

    setLogoUploading(true)
    try {
      const result = await adminApi.uploadImage(file, 'logo')
      setInfoForm((prev) => ({ ...prev, logoUrl: result.url }))
      setLogoPreview(result.url)
    } catch (error) {
      console.error('Failed to upload logo:', error)
      alert('로고 업로드에 실패했습니다.')
      setLogoPreview(organization.logoUrl || null)
    } finally {
      setLogoUploading(false)
    }
  }

  const handleSaveSection = () => {
    if (organization) {
      updateMutation.mutate({ id: organization.id, data: infoForm })
    }
  }

  const [showResetDataModal, setShowResetDataModal] = useState(false)

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">기관 정보를 불러오는 중...</p>
      </div>
    )
  }

  if (error || !organization) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">기관을 찾을 수 없습니다</p>
        <button onClick={() => navigate('/organizations')} className="btn-secondary btn-md mt-4">
          기관 목록으로 돌아가기
        </button>
      </div>
    )
  }

  const tabs = [
    { key: 'info' as const, label: '기본 정보' },
    { key: 'licenses' as const, label: '라이선스' },
    { key: 'members' as const, label: '멤버' },
    { key: 'contract' as const, label: '계약' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/organizations')}
          className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
          title="목록으로"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-4 flex-1">
          {organization.logoUrl ? (
            <img src={organization.logoUrl} alt="" className="w-12 h-12 rounded-lg object-cover" />
          ) : (
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: organization.primaryColor || '#3B82F6' }}
            >
              <Building2 className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{organization.name}</h1>
            <p className="text-sm text-slate-500">
              {organization.subdomain}.{organization.organizationType === 'learning_center' ? 'quebonLC.tv' : 'aiqlab.tv'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDeactivate}
            disabled={!organization.isActive || deactivateMutation.isPending}
            className="btn-md inline-flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4" />
            비활성화
          </button>
          <button
            onClick={() => setShowResetDataModal(true)}
            className="btn-md inline-flex items-center gap-2 px-4 py-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Data
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="flex border-b border-slate-200">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Info Tab */}
          {activeTab === 'info' && (() => {
            const sd = editingSection === 'access' ? (infoForm.subdomain || '') : (organization.subdomain || '')
            const ot = organization.organizationType

            const sectionBtn = (section: EditSection) =>
              editingSection === section ? (
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditingSection(null)} className="text-xs text-slate-400 hover:text-slate-600">취소</button>
                  <button onClick={handleSaveSection} disabled={updateMutation.isPending} className="text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1">
                    {updateMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                    저장
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => startEditSection(section)}
                  disabled={editingSection !== null}
                  className="text-xs text-slate-400 hover:text-slate-600 disabled:opacity-0 disabled:cursor-default"
                >
                  수정
                </button>
              )

            return (
            <div className="space-y-6">
              {/* 기본 정보 */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700">기본 정보</h3>
                    {sectionBtn('basic')}
                  </div>
                  <dl className="space-y-3">
                    <div className="flex items-center">
                      <dt className="w-28 text-sm text-slate-500 shrink-0">기관명</dt>
                      <dd className="flex-1">
                        {editingSection === 'basic' ? (
                          <input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.name || ''} onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })} />
                        ) : (
                          <span className="text-sm font-medium text-slate-900">{organization.name}</span>
                        )}
                      </dd>
                    </div>
                    <div className="flex items-center">
                      <dt className="w-28 text-sm text-slate-500 shrink-0">표시명</dt>
                      <dd className="flex-1">
                        {editingSection === 'basic' ? (
                          <input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.displayName || ''} onChange={(e) => setInfoForm({ ...infoForm, displayName: e.target.value })} />
                        ) : (
                          <span className="text-sm text-slate-700">{organization.displayName || '-'}</span>
                        )}
                      </dd>
                    </div>
                    <div className="flex items-center">
                      <dt className="w-28 text-sm text-slate-500 shrink-0">대표자</dt>
                      <dd className="flex-1">
                        {editingSection === 'basic' ? (
                          <input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.representativeName || ''} onChange={(e) => setInfoForm({ ...infoForm, representativeName: e.target.value })} />
                        ) : (
                          <span className="text-sm text-slate-700">{organization.representativeName || '-'}</span>
                        )}
                      </dd>
                    </div>
                    <div className="flex items-center">
                      <dt className="w-28 text-sm text-slate-500 shrink-0">유형</dt>
                      <dd className="flex-1">
                        {editingSection === 'basic' ? (
                          <select className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.organizationType || 'company'} onChange={(e) => setInfoForm({ ...infoForm, organizationType: e.target.value })}>
                            {ORGANIZATION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        ) : (
                          <span className="text-sm text-slate-700">{ORGANIZATION_TYPE_LABELS[organization.organizationType] || organization.organizationType}</span>
                        )}
                      </dd>
                    </div>
                    <div className="flex items-center">
                      <dt className="w-28 text-sm text-slate-500 shrink-0">업종</dt>
                      <dd className="flex-1">
                        {editingSection === 'basic' ? (
                          <input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.businessCategory || ''} onChange={(e) => setInfoForm({ ...infoForm, businessCategory: e.target.value })} placeholder="예: IT/소프트웨어" />
                        ) : (
                          <span className="text-sm text-slate-700">{organization.businessCategory || '-'}</span>
                        )}
                      </dd>
                    </div>
                    <div className="flex items-center">
                      <dt className="w-28 text-sm text-slate-500 shrink-0">언어 설정</dt>
                      <dd className="flex-1">
                        {editingSection === 'basic' ? (
                          <select className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.languageMode || 'ko'} onChange={(e) => setInfoForm({ ...infoForm, languageMode: e.target.value })}>
                            {LANGUAGE_MODE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                          </select>
                        ) : (
                          <span className="text-sm text-slate-700">{LANGUAGE_MODE_LABELS[organization.languageMode] || '한국어'}</span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* 연락처 */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700">연락처</h3>
                    {sectionBtn('contact')}
                  </div>
                  <dl className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                      {editingSection === 'contact' ? (
                        <input type="email" className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.email || ''} onChange={(e) => setInfoForm({ ...infoForm, email: e.target.value })} />
                      ) : (
                        <span className="text-sm text-slate-700">{organization.email || '-'}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                      {editingSection === 'contact' ? (
                        <input type="tel" className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.phoneNumber || ''} onChange={(e) => setInfoForm({ ...infoForm, phoneNumber: e.target.value })} />
                      ) : (
                        <span className="text-sm text-slate-700">{organization.phoneNumber || '-'}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                      {editingSection === 'contact' ? (
                        <input type="url" className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.websiteUrl || ''} onChange={(e) => setInfoForm({ ...infoForm, websiteUrl: e.target.value })} placeholder="https://" />
                      ) : organization.websiteUrl ? (
                        <a href={organization.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">{organization.websiteUrl}</a>
                      ) : (
                        <span className="text-sm text-slate-700">-</span>
                      )}
                    </div>
                  </dl>
                </div>
              </div>

              {/* 접속 정보 */}
              {(organization.subdomain || editingSection === 'access') && (
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700">접속 정보</h3>
                    {sectionBtn('access')}
                  </div>
                  {editingSection === 'access' && (
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-sm text-slate-500 w-20 shrink-0">서브도메인</span>
                      <input
                        type="text"
                        className="border border-slate-300 rounded px-2 py-1 text-sm w-40"
                        value={infoForm.subdomain || ''}
                        onChange={(e) => setInfoForm({ ...infoForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        placeholder={ot === 'learning_center' ? 'seoul101' : 'lgcns'}
                      />
                      <span className="text-sm text-slate-500">{ot === 'learning_center' ? '.quebonLC.tv' : '.aiqlab.tv'}</span>
                    </div>
                  )}
                  {sd && (
                    <div className="bg-slate-50 rounded-lg p-4 space-y-2.5">
                      <div className="flex items-center gap-3">
                        <span className="w-20 text-xs font-medium text-slate-500 uppercase tracking-wide">Production</span>
                        <a
                          href={`https://${getFullDomain(sd, ot)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-mono bg-green-100 text-green-800 px-2.5 py-1 rounded hover:bg-green-200 transition-colors"
                        >
                          {getFullDomain(sd, ot)}
                        </a>
                      </div>
                      {getStagingUrl(sd, ot) && (
                        <div className="flex items-center gap-3">
                          <span className="w-20 text-xs font-medium text-slate-500 uppercase tracking-wide">Staging</span>
                          <a
                            href={`https://${getStagingUrl(sd, ot)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-mono bg-purple-100 text-purple-800 px-2.5 py-1 rounded hover:bg-purple-200 transition-colors"
                          >
                            {getStagingUrl(sd, ot)}
                          </a>
                        </div>
                      )}
                      {getDevUrl(sd, ot) && (
                        <div className="flex items-center gap-3">
                          <span className="w-20 text-xs font-medium text-slate-500 uppercase tracking-wide">Local</span>
                          <a
                            href={`http://${getDevUrl(sd, ot)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-mono bg-amber-100 text-amber-800 px-2.5 py-1 rounded hover:bg-amber-200 transition-colors"
                          >
                            {getDevUrl(sd, ot)}
                          </a>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <span className="w-20 text-xs font-medium text-slate-500 uppercase tracking-wide">API</span>
                        <code className="text-sm font-mono bg-slate-200 text-slate-700 px-2.5 py-1 rounded">
                          localhost:8080
                        </code>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 계약 정보 + 브랜딩 */}
              <div className="grid grid-cols-2 gap-6 pt-4 border-t">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700">계약 정보</h3>
                    {sectionBtn('contract')}
                  </div>
                  <dl className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                      {editingSection === 'contract' ? (
                        <div className="flex items-center gap-1.5 flex-1">
                          <input type="date" className="border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.contractStartDate || ''} onChange={(e) => setInfoForm({ ...infoForm, contractStartDate: e.target.value })} />
                          <span className="text-slate-400">~</span>
                          <input type="date" className="border border-slate-300 rounded px-2 py-1 text-sm" value={infoForm.contractEndDate || ''} onChange={(e) => setInfoForm({ ...infoForm, contractEndDate: e.target.value })} />
                        </div>
                      ) : (
                        <span className="text-sm text-slate-700">
                          {organization.contractStartDate || '미정'} ~ {organization.contractEndDate || '미정'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400 shrink-0" />
                      {editingSection === 'contract' ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-slate-500">Max Seats:</span>
                          <input type="number" className="w-24 border border-slate-300 rounded px-2 py-1 text-sm" min="0" value={infoForm.maxSeats ?? 0} onChange={(e) => setInfoForm({ ...infoForm, maxSeats: parseInt(e.target.value) || 0 })} />
                        </div>
                      ) : (
                        <span className="text-sm text-slate-700">
                          Max Seats: {organization.maxSeats || '무제한'}
                        </span>
                      )}
                    </div>
                  </dl>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700">브랜딩</h3>
                    {sectionBtn('branding')}
                  </div>
                  <dl className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500 w-16 shrink-0">로고</span>
                      {editingSection === 'branding' ? (
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
                            {logoPreview ? (
                              <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                            ) : (
                              <Image className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                          <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 cursor-pointer">
                            {logoUploading ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Upload className="w-3.5 h-3.5" />
                            )}
                            {logoUploading ? '업로드 중...' : '로고 업로드'}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                              className="hidden"
                              onChange={handleLogoUpload}
                              disabled={logoUploading}
                            />
                          </label>
                        </div>
                      ) : (
                        organization.logoUrl ? (
                          <img src={organization.logoUrl} alt="" className="w-10 h-10 rounded-lg object-contain border" />
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Palette className="w-4 h-4 text-slate-400 shrink-0" />
                      {editingSection === 'branding' ? (
                        <>
                          <input
                            type="color"
                            className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
                            value={infoForm.primaryColor || '#3B82F6'}
                            onChange={(e) => setInfoForm({ ...infoForm, primaryColor: e.target.value })}
                          />
                          <input
                            type="text"
                            className="w-28 border border-slate-300 rounded px-2 py-1 text-sm"
                            value={infoForm.primaryColor || ''}
                            onChange={(e) => setInfoForm({ ...infoForm, primaryColor: e.target.value })}
                            placeholder="#3B82F6"
                          />
                        </>
                      ) : (
                        <>
                          <div
                            className="w-6 h-6 rounded border"
                            style={{ backgroundColor: organization.primaryColor || '#3B82F6' }}
                          />
                          <span className="text-sm text-slate-700">{organization.primaryColor || '#3B82F6'}</span>
                        </>
                      )}
                    </div>
                  </dl>
                </div>
              </div>

              {/* 상태 */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">상태</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    organization.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {organization.isActive ? '활성' : '비활성'}
                  </span>
                </div>
              </div>
            </div>
            )
          })()}

          {/* Licenses Tab */}
          {activeTab === 'licenses' && (() => {
            const purchasedLicenses = licenses.filter(l => l.purchasedAt != null)
            const availableLicenses = licenses.filter(l => l.purchasedAt == null)
            return (
            <div className="space-y-6">
              {/* 구매 완료 라이선스 */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-700">구매 완료 라이선스</h3>
                </div>
                {licensesLoading ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : purchasedLicenses.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-sm border border-dashed rounded-lg">
                    구매 완료된 라이선스가 없습니다.
                  </div>
                ) : (
                  <div className="border rounded-lg divide-y">
                    {purchasedLicenses.map((license) => (
                      <div key={license.id} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{license.productName || `상품 #${license.productId}`}</p>
                          <p className="text-sm text-slate-500">
                            {license.validFrom} ~ {license.validUntil || '무기한'}
                            {' · '}
                            좌석: {license.usedSeats}/{license.purchasedSeats || '무제한'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            license.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {license.isActive ? '활성' : '비활성'}
                          </span>
                          <button
                            onClick={() => handleRevokeLicense(license.id)}
                            disabled={revokeLicenseMutation.isPending}
                            className="p-1.5 hover:bg-red-50 rounded text-red-600"
                            title="취소"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 구매 가능 상품 */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700">구매 가능 상품</h3>

                {licensesLoading ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : availableLicenses.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-sm border border-dashed rounded-lg">
                    구매 가능한 상품이 없습니다.
                  </div>
                ) : (
                  <div className="border border-amber-200 rounded-lg divide-y divide-amber-100 bg-amber-50/30">
                    {availableLicenses.map((license) => (
                      <div key={license.id} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{license.productName || `상품 #${license.productId}`}</p>
                          <p className="text-sm text-slate-500">
                            {license.validFrom} ~ {license.validUntil || '무기한'}
                            {' · '}
                            좌석: {license.usedSeats}/{license.purchasedSeats || '무제한'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            미구매
                          </span>
                          <button
                            onClick={() => handleRevokeLicense(license.id)}
                            disabled={revokeLicenseMutation.isPending}
                            className="p-1.5 hover:bg-red-50 rounded text-red-600"
                            title="삭제"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )
          })()}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">멤버 목록</h3>
                <button
                  onClick={() => setShowAddMemberModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  <UserPlus className="w-4 h-4" />
                  멤버 추가
                </button>
              </div>

              {membersLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : members.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  등록된 멤버가 없습니다.
                </div>
              ) : (
                <div className="border rounded-lg overflow-visible">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700 rounded-tl-lg">이름</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">이메일</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">부서</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">역할</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-slate-700 rounded-tr-lg">작업</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {members.map((member) => (
                        <tr key={member.userId} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{member.fullName}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{member.email}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{member.departmentName || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              member.role === 'admin' || member.role === 'owner'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {member.role === 'admin' ? '관리자' : member.role === 'owner' ? '관리자겸 학습자' : '학습자'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <MemberActionMenu
                              member={member}
                              onResetPassword={() => handleResetPassword(member)}
                              onChangeRole={() => setRoleModal({ member, selectedRole: member.role })}
                              onRemoveFromOrg={() => setConfirmModal({ type: 'removeMember', member })}
                              onDeleteUser={() => setConfirmModal({ type: 'deleteUser', member })}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Add Member Modal */}
              {showAddMemberModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowAddMemberModal(false)}>
                  <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-6 py-4 border-b">
                      <h3 className="text-lg font-semibold text-slate-900">멤버 추가</h3>
                      <button onClick={() => setShowAddMemberModal(false)} className="p-1 rounded-lg hover:bg-slate-100">
                        <X className="w-5 h-5 text-slate-500" />
                      </button>
                    </div>

                    {/* Tab Toggle */}
                    <div className="flex border-b border-slate-200">
                      <button
                        onClick={() => setAddMemberMode('search')}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                          addMemberMode === 'search'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Search className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                        기존 사용자 검색
                      </button>
                      <button
                        onClick={() => setAddMemberMode('create')}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                          addMemberMode === 'create'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <UserPlus className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                        새 사용자 생성
                      </button>
                    </div>

                    <div className="p-6 space-y-4 overflow-y-auto flex-1">
                      {/* Role Selection (공통) */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">역할:</span>
                        <select
                          value={selectedRole}
                          onChange={e => setSelectedRole(e.target.value)}
                          className="px-3 py-1.5 border rounded-lg text-sm bg-white"
                        >
                          <option value="member">학습자</option>
                          <option value="admin">관리자</option>
                          <option value="owner">관리자겸 학습자</option>
                        </select>
                      </div>

                      {addMemberMode === 'search' ? (
                        <>
                          {/* Search */}
                          <div className="flex gap-2">
                            <div className="flex-1 relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <input
                                type="text"
                                placeholder="이름 또는 이메일로 검색 (2글자 이상)"
                                value={memberSearchKeyword}
                                onChange={e => setMemberSearchKeyword(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <select
                              value={memberSearchType}
                              onChange={e => setMemberSearchType(e.target.value)}
                              className="px-3 py-2 border rounded-lg text-sm bg-white"
                            >
                              <option value="">전체 유형</option>
                              <option value="student">학생</option>
                              <option value="employee">기업 (B2B)</option>
                              <option value="teacher">Learning Center</option>
                              <option value="admin">깨봉 Admin</option>
                            </select>
                          </div>

                          {/* Search Results */}
                          {memberSearchKeyword.length < 2 ? (
                            <div className="text-center py-8 text-slate-400 text-sm">
                              검색어를 2글자 이상 입력해주세요.
                            </div>
                          ) : searchUsersLoading ? (
                            <div className="flex justify-center py-8">
                              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                            </div>
                          ) : !searchUsersResult?.content?.length ? (
                            <div className="text-center py-8 text-slate-400 text-sm">
                              <p>검색 결과가 없습니다.</p>
                              <button
                                onClick={() => {
                                  setAddMemberMode('create')
                                  if (memberSearchKeyword.includes('@')) {
                                    setNewUserForm(prev => ({ ...prev, email: memberSearchKeyword }))
                                  }
                                }}
                                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                              >
                                <UserPlus className="w-4 h-4" />
                                새 사용자로 생성하기
                              </button>
                            </div>
                          ) : (
                            <div className="border rounded-lg overflow-hidden">
                              <table className="w-full">
                                <thead className="bg-slate-50">
                                  <tr>
                                    <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">이름</th>
                                    <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">이메일</th>
                                    <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">유형</th>
                                    <th className="text-right px-4 py-2 text-xs font-medium text-slate-600">작업</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {searchUsersResult.content.map((user: AdminUser) => {
                                    const isAlreadyMember = memberUserIds.has(user.id)
                                    return (
                                      <tr key={user.id} className="hover:bg-slate-50">
                                        <td className="px-4 py-2 text-sm font-medium text-slate-900">{user.fullName}</td>
                                        <td className="px-4 py-2 text-sm text-slate-600">{user.email}</td>
                                        <td className="px-4 py-2 text-sm text-slate-600">{USER_TYPE_LABELS[user.userType] || user.userType}</td>
                                        <td className="px-4 py-2 text-right">
                                          {isAlreadyMember ? (
                                            <span className="text-xs text-slate-400">이미 등록됨</span>
                                          ) : (
                                            <button
                                              onClick={() => handleAddMember(user.id)}
                                              disabled={addMemberMutation.isPending}
                                              className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                                            >
                                              {addMemberMutation.isPending ? '추가 중...' : '추가'}
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </>
                      ) : (
                        /* Create New User Form */
                        <form onSubmit={handleCreateAndAddMember} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">이메일 *</label>
                            <input
                              type="email"
                              value={newUserForm.email}
                              onChange={e => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="user@example.com"
                              className="w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">이름 *</label>
                            <input
                              type="text"
                              value={newUserForm.fullName}
                              onChange={e => setNewUserForm(prev => ({ ...prev, fullName: e.target.value }))}
                              placeholder="홍길동"
                              className="w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">사용자 유형</label>
                            <select
                              value={newUserForm.userType}
                              onChange={e => setNewUserForm(prev => ({ ...prev, userType: e.target.value as any }))}
                              className="w-full px-4 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="employee">기업 (B2B)</option>
                              <option value="student">학생</option>
                              <option value="teacher">Learning Center</option>
                              <option value="admin">깨봉 Admin</option>
                              <option value="individual">개인 (B2C)</option>
                              <option value="parent">학부모</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">비밀번호</label>
                            <input
                              type="text"
                              value={newUserForm.password}
                              onChange={e => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                              placeholder="미입력 시 기본 비밀번호 부여"
                              className="w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-slate-400 mt-1">비워두면 서버에서 기본 비밀번호가 설정됩니다.</p>
                          </div>

                          <button
                            type="submit"
                            disabled={createAndAddMemberMutation.isPending}
                            className="w-full py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {createAndAddMemberMutation.isPending ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                생성 중...
                              </>
                            ) : (
                              <>
                                <UserPlus className="w-4 h-4" />
                                사용자 생성 및 멤버 추가
                              </>
                            )}
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contract Tab */}
          {activeTab === 'contract' && (
            <ContractTab organizationId={organization.id} members={members} />
          )}
        </div>
      </div>

      {/* Confirm Modal for member actions */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]" onClick={() => setConfirmModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  confirmModal.type === 'deleteUser' ? 'bg-red-100' : 'bg-amber-100'
                }`}>
                  {confirmModal.type === 'deleteUser'
                    ? <UserX className="w-5 h-5 text-red-600" />
                    : <UserMinus className="w-5 h-5 text-amber-600" />
                  }
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {confirmModal.type === 'removeMember' ? '조직에서 멤버 제거' : '사용자 완전 삭제'}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {confirmModal.member.fullName} ({confirmModal.member.email})
                  </p>
                </div>
              </div>

              <div className={`p-3 rounded-lg text-sm ${
                confirmModal.type === 'deleteUser' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'
              }`}>
                {confirmModal.type === 'removeMember'
                  ? '이 멤버를 조직에서만 제거합니다. 사용자 계정은 유지됩니다.'
                  : '이 사용자를 조직에서 제거하고 사용자 계정까지 완전히 삭제합니다. 이 작업은 되돌릴 수 없습니다.'
                }
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setConfirmModal(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
                >
                  취소
                </button>
                <button
                  onClick={() => {
                    if (confirmModal.type === 'removeMember') {
                      removeMemberMutation.mutate(confirmModal.member.userId)
                    } else {
                      deleteUserMutation.mutate(confirmModal.member.userId)
                    }
                    setConfirmModal(null)
                  }}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg ${
                    confirmModal.type === 'deleteUser'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      {roleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]" onClick={() => setRoleModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">역할 변경</h3>
                  <p className="text-sm text-slate-500">
                    {roleModal.member.fullName} ({roleModal.member.email})
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {[
                  { value: 'member', label: '학습자' },
                  { value: 'admin', label: '관리자' },
                  { value: 'owner', label: '관리자겸 학습자' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      roleModal.selectedRole === opt.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={opt.value}
                      checked={roleModal.selectedRole === opt.value}
                      onChange={() => setRoleModal({ ...roleModal, selectedRole: opt.value })}
                      className="accent-blue-600"
                    />
                    <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                  </label>
                ))}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setRoleModal(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
                >
                  취소
                </button>
                <button
                  onClick={() => {
                    updateRoleMutation.mutate({
                      userId: roleModal.member.userId,
                      role: roleModal.selectedRole,
                    })
                    setRoleModal(null)
                  }}
                  disabled={roleModal.selectedRole === roleModal.member.role}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Data Modal */}
      {showResetDataModal && (
        <ResetDataModal
          organization={organization}
          onClose={() => setShowResetDataModal(false)}
        />
      )}
    </div>
  )
}

function MemberActionMenu({
  member,
  onResetPassword,
  onChangeRole,
  onRemoveFromOrg,
  onDeleteUser,
}: {
  member: OrganizationMember
  onResetPassword: () => void
  onChangeRole: () => void
  onRemoveFromOrg: () => void
  onDeleteUser: () => void
}) {
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const menuHeight = 180
      const dropUp = rect.bottom + menuHeight > window.innerHeight
      setMenuPos({
        top: dropUp ? rect.top - menuHeight : rect.bottom + 4,
        left: Math.max(8, rect.right - 192),
      })
    }
    setOpen(!open)
  }

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open && menuPos && (
        <div
          className="fixed z-50 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <button
            onClick={() => { setOpen(false); onResetPassword() }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <KeyRound className="w-4 h-4 text-blue-500" />
            비밀번호 리셋
          </button>
          <button
            onClick={() => { setOpen(false); onChangeRole() }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <Shield className="w-4 h-4 text-indigo-500" />
            역할 변경
          </button>
          <div className="border-t border-slate-100 my-1" />
          <button
            onClick={() => { setOpen(false); onRemoveFromOrg() }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50"
          >
            <UserMinus className="w-4 h-4" />
            조직에서만 삭제
          </button>
          <button
            onClick={() => { setOpen(false); onDeleteUser() }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <UserX className="w-4 h-4" />
            User 까지 삭제
          </button>
        </div>
      )}
    </div>
  )
}
